
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { loanAPI, debicheckAPI } from '../../services/api';
import PaymentMethodModal from '../payment/PaymentMethodModal';
import './StudentDashboard.css';

type DashboardState = 'ELIGIBLE' | 'PENDING_LOAN' | 'AWAITING_DISBURSEMENT' | 'ACTIVE_LOAN' | 'APPLICATION_FEEDBACK' | 'LOADING';

interface LoanData {
  id: string;
  status: string;
  principalAmount: number;
  interestRate: number;
  interestAmount: number;
  adminFee: number;
  totalAmount: number;
  totalPaid: number;
  outstandingBalance: number;
  repaymentDate: string;
  applicationDate?: string;
  loanNumber: string;
  /** Set when application is rejected or requires_info (admin feedback) */
  rejectionReason?: string;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  nsfas_statement: 'NSFAS statement',
  bank_statement: 'Bank statement',
  student_card: 'Student card',
  id_document: 'ID document',
  other: 'Document',
};

interface PaymentData {
  id: string;
  status: string;
  amount: number;
  dueDate?: string;
  paymentMethod?: string;
}

interface RecentActivityItem {
  id: string;
  title: string;
  subtitle: string;
  date: string;
  amount?: number;
  tone: 'success' | 'info';
}

interface InstallmentInfo {
  total: number;
  completed: number;
  pending: number;
}

interface EligibilityData {
  eligible: boolean;
  reasons: string[];
  activeLoans: number;
  hasPendingApplication: boolean;
  totalOutstanding: number;
  maxAllowedAmount: number;
}

// Professional Icons components
const Icons = {
  Wallet: () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
      <path d="M4 6v12c0 1.1.9 2 2 2h14v-4" />
      <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z" />
    </svg>
  ),
  CreditCard: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  Clock: () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  Calculator: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="16" y1="14" x2="16" y2="18" />
      <path d="M16 10h.01" />
      <path d="M12 10h.01" />
      <path d="M8 10h.01" />
      <path d="M12 14h.01" />
      <path d="M8 14h.01" />
      <path d="M12 18h.01" />
      <path d="M8 18h.01" />
    </svg>
  ),
  History: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" />
      <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
    </svg>
  ),
  Lightbulb: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M15.09 14c.18-.9.27-1.48.69-2.13a6.04 6.04 0 0 0-1.13-7.6C13.2 3.03 11.36 3 10 3c-1.3 0-2.7.3-3.6 1.2a6 6 0 0 0-1 7.7c.4.63.5 1.2.7 2.1" />
    </svg>
  ),
  User: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  ChevronDown: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Info: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  HelpCircle: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  AlertCircle: () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  Calendar: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Lock: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  ChevronRight: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  ArrowRight: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  Message: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Headset: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
  ),
  CheckCircle: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
};

const StudentDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<DashboardState>('LOADING');
  const [activeLoan, setActiveLoan] = useState<LoanData | null>(null);
  const [pendingPayment, setPendingPayment] = useState<PaymentData | null>(null);
  const [installments, setInstallments] = useState<InstallmentInfo | null>(null);
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [eligibility, setEligibility] = useState<EligibilityData | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivityItem[]>([]);
  
  // Payment Modal
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [settling, setSettling] = useState(false);
  const [debicheckPrompt, setDebicheckPrompt] = useState<
    'none' | 'setup' | 'pending' | 'authorised'
  >('none');

  /** Admin decision on a closed application (rejected / needs more info) */
  const [feedbackLoan, setFeedbackLoan] = useState<LoanData | null>(null);
  const [feedbackDocuments, setFeedbackDocuments] = useState<any[]>([]);
  /** While status is still pending, documents already marked rejected by admin */
  const [pendingDocIssues, setPendingDocIssues] = useState<{ label: string; reason?: string }[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch loan data and eligibility in parallel
      const [paymentsRes, eligibilityRes] = await Promise.all([
        loanAPI.getStudentPayments(),
        loanAPI.checkEligibility().catch(() => null),
      ]);

      if (eligibilityRes?.success) {
        setEligibility(eligibilityRes.data);
      }

      if (paymentsRes.success && paymentsRes.data) {
        const { loans, payments } = paymentsRes.data;

        // Lightweight activity from data we already have (no third-party APIs)
        const activity: RecentActivityItem[] = [];
        const completedPayments = payments
          .filter((p: any) => p.status === 'completed')
          .sort((a: any, b: any) =>
            new Date(b.paidDate || b.updatedAt || b.createdAt || 0).getTime() -
            new Date(a.paidDate || a.updatedAt || a.createdAt || 0).getTime()
          )
          .slice(0, 3);

        completedPayments.forEach((p: any) => {
          activity.push({
            id: `pay-${p.id}`,
            title: 'Payment received',
            subtitle: p.reference ? `Ref ${p.reference}` : 'Installment paid',
            date: p.paidDate || p.updatedAt || p.createdAt,
            amount: p.amount,
            tone: 'success',
          });
        });

        const approvedLoan = loans.find((l: any) =>
          ['approved', 'disbursed', 'overdue', 'repaid'].includes(l.status)
        );
        if (approvedLoan?.applicationDate || approvedLoan?.createdAt) {
          activity.push({
            id: `loan-approved-${approvedLoan.id}`,
            title: 'Loan approved',
            subtitle: `Loan #${approvedLoan.loanNumber}`,
            date: approvedLoan.applicationDate || approvedLoan.createdAt,
            amount: approvedLoan.totalAmount,
            tone: 'success',
          });
        }

        const pendingDebit = payments.find(
          (p: any) => p.status === 'pending' && (p.paymentMethod === 'debit_order' || p.paymentMethod === 'debicheck')
        );
        if (pendingDebit) {
          activity.push({
            id: `debit-${pendingDebit.id}`,
            title: 'Debit order scheduled',
            subtitle: 'Next installment',
            date: pendingDebit.dueDate || pendingDebit.createdAt,
            amount: pendingDebit.amount,
            tone: 'info',
          });
        }

        setRecentActivity(
          activity
            .filter((a) => a.date)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .slice(0, 4)
        );

        // Active repayable loan: funds already sent (disbursed/overdue) and balance owing.
        // Approved-but-not-disbursed loans wait for admin disbursement — no Make Payment yet.
        const currentLoan = loans.find((l: any) => {
          if (!['disbursed', 'overdue'].includes(l.status)) return false;
          const outstanding = (l.outstandingBalance ?? l.totalAmount);
          return outstanding > 0;
        });

        const awaitingDisbursement = loans.find((l: any) => l.status === 'approved');

        const recentlyRepaid = loans.find((l: any) =>
          l.status === 'repaid' ||
          (['approved', 'disbursed'].includes(l.status) && (l.outstandingBalance ?? l.totalAmount) <= 0)
        );

        const pendingApp = loans.find((l: any) => l.status === 'pending');
        const feedbackApplication = loans.find((l: any) =>
          ['rejected', 'requires_info'].includes(l.status)
        );

        const clearFeedback = () => {
          setFeedbackLoan(null);
          setFeedbackDocuments([]);
          setPendingDocIssues([]);
        };

        if (currentLoan) {
          clearFeedback();
          const loanPayments = payments.filter((p: any) => p.loanId === currentLoan.id);
          const completedCount = loanPayments.filter((p: any) => p.status === 'completed').length;
          const pendingCount = loanPayments.filter((p: any) => p.status === 'pending').length;
          const totalInstallments = completedCount + pendingCount;
          if (totalInstallments > 0) {
            setInstallments({ total: totalInstallments, completed: completedCount, pending: pendingCount });
          } else {
            setInstallments(null);
          }

          const payment = payments.find((p: any) => p.status === 'pending' && p.loanId === currentLoan.id);

          setActiveLoan({
            id: currentLoan.id,
            status: currentLoan.status,
            principalAmount: currentLoan.principalAmount || 0,
            interestRate: currentLoan.interestRate || 15,
            interestAmount: currentLoan.interestAmount || 0,
            adminFee: currentLoan.adminFee || 0,
            totalAmount: currentLoan.totalAmount,
            totalPaid: currentLoan.totalPaid || 0,
            outstandingBalance: currentLoan.outstandingBalance ?? currentLoan.totalAmount,
            repaymentDate: currentLoan.repaymentDate || '',
            applicationDate: currentLoan.applicationDate,
            loanNumber: currentLoan.loanNumber
          });
          if (payment) {
            setPendingPayment({
              id: payment.id,
              status: payment.status,
              amount: payment.amount,
              dueDate: payment.dueDate,
              paymentMethod: payment.paymentMethod,
            });
          } else {
            setPendingPayment(null);
          }

          try {
            const mandateRes = await debicheckAPI.getMandateForLoan(currentLoan.id);
            const m = mandateRes.data;
            if (!m) setDebicheckPrompt('setup');
            else if (m.status === 'pending') setDebicheckPrompt('pending');
            else if (m.status === 'authenticated' || m.status === 'rms_registered') {
              setDebicheckPrompt('authorised');
            } else setDebicheckPrompt('setup');
          } catch {
            setDebicheckPrompt('setup');
          }

          setState('ACTIVE_LOAN');
          return;
        }

        setPendingPayment(null);
        setInstallments(null);
        setDebicheckPrompt('none');

        if (awaitingDisbursement) {
          clearFeedback();
          try {
            const mandateRes = await debicheckAPI.getMandateForLoan(awaitingDisbursement.id);
            const m = mandateRes.data;
            if (!m) setDebicheckPrompt('setup');
            else if (m.status === 'pending') setDebicheckPrompt('pending');
            else if (m.status === 'authenticated' || m.status === 'rms_registered') {
              setDebicheckPrompt('authorised');
            } else setDebicheckPrompt('setup');
          } catch {
            setDebicheckPrompt('setup');
          }
          setActiveLoan({
            id: awaitingDisbursement.id,
            status: awaitingDisbursement.status,
            principalAmount: awaitingDisbursement.principalAmount || 0,
            interestRate: awaitingDisbursement.interestRate || 15,
            interestAmount: awaitingDisbursement.interestAmount || 0,
            adminFee: awaitingDisbursement.adminFee || 0,
            totalAmount: awaitingDisbursement.totalAmount,
            totalPaid: awaitingDisbursement.totalPaid || 0,
            outstandingBalance: awaitingDisbursement.outstandingBalance ?? awaitingDisbursement.totalAmount,
            repaymentDate: awaitingDisbursement.repaymentDate || '',
            applicationDate: awaitingDisbursement.applicationDate,
            loanNumber: awaitingDisbursement.loanNumber,
          });
          setState('AWAITING_DISBURSEMENT');
          return;
        }

        if (pendingApp) {
          clearFeedback();
          let issues: { label: string; reason?: string }[] = [];
          try {
            const det = await loanAPI.getApplicationDetails(pendingApp.id);
            const docs = det.data?.documents || [];
            issues = docs
              .filter((d: any) => d.verification_status === 'rejected')
              .map((d: any) => ({
                label: DOC_TYPE_LABELS[d.document_type] || d.document_type,
                reason: d.rejection_reason || undefined,
              }));
          } catch {
            /* non-fatal */
          }
          setPendingDocIssues(issues);
          setActiveLoan(null);
          setState('PENDING_LOAN');
          return;
        }

        if (feedbackApplication) {
          setPendingDocIssues([]);
          let docs: any[] = [];
          try {
            const det = await loanAPI.getApplicationDetails(feedbackApplication.id);
            docs = det.data?.documents || [];
          } catch {
            /* non-fatal */
          }
          setFeedbackDocuments(docs);
          setFeedbackLoan({
            id: feedbackApplication.id,
            status: feedbackApplication.status,
            principalAmount: feedbackApplication.principalAmount || 0,
            interestRate: feedbackApplication.interestRate || 15,
            interestAmount: feedbackApplication.interestAmount || 0,
            adminFee: feedbackApplication.adminFee || 0,
            totalAmount: feedbackApplication.totalAmount,
            totalPaid: feedbackApplication.totalPaid || 0,
            outstandingBalance: feedbackApplication.outstandingBalance ?? 0,
            repaymentDate: feedbackApplication.repaymentDate || '',
            applicationDate: feedbackApplication.applicationDate,
            loanNumber: feedbackApplication.loanNumber,
            rejectionReason: feedbackApplication.rejectionReason,
          });
          setActiveLoan(null);
          setState('APPLICATION_FEEDBACK');
          return;
        }

        clearFeedback();
        if (recentlyRepaid) {
          setActiveLoan({
            id: recentlyRepaid.id,
            status: recentlyRepaid.status,
            principalAmount: recentlyRepaid.principalAmount || 0,
            interestRate: recentlyRepaid.interestRate || 15,
            interestAmount: recentlyRepaid.interestAmount || 0,
            adminFee: recentlyRepaid.adminFee || 0,
            totalAmount: recentlyRepaid.totalAmount,
            totalPaid: recentlyRepaid.totalPaid || 0,
            outstandingBalance: 0,
            repaymentDate: recentlyRepaid.repaymentDate || '',
            applicationDate: recentlyRepaid.applicationDate,
            loanNumber: recentlyRepaid.loanNumber
          });
        } else {
          setActiveLoan(null);
        }
        setState('ELIGIBLE');
      } else {
        /* no loan payload — eligibility view still renders */
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
      setState('ELIGIBLE');
    }
  };

  const handleApply = () => navigate('/apply');
  
  const formatMoney = (amount: number) =>
    new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);

  const formatShortDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const daysUntil = (dateString?: string) => {
    if (!dateString) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dateString);
    due.setHours(0, 0, 0, 0);
    return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const paidPercent = activeLoan
    ? Math.min(
        100,
        Math.round(
          (activeLoan.totalPaid / Math.max(activeLoan.totalAmount, 1)) * 100
        )
      )
    : 0;

  const remainingAfterPayment = activeLoan && pendingPayment
    ? Math.max(0, activeLoan.outstandingBalance - pendingPayment.amount)
    : activeLoan?.outstandingBalance ?? 0;

  const nextDueDays = daysUntil(pendingPayment?.dueDate || activeLoan?.repaymentDate);

  const canSettleEarly = Boolean(
    activeLoan &&
    activeLoan.outstandingBalance > 0.5 &&
    (!pendingPayment || activeLoan.outstandingBalance > pendingPayment.amount + 0.5)
  );

  const handleSettleEarly = async () => {
    if (!activeLoan || settling) return;
    setSettling(true);
    try {
      const res = await loanAPI.settleLoan(activeLoan.id);
      navigate(`/payment/${res.data.paymentId}/payfast`);
    } catch (error: any) {
      const message = error.response?.data?.error || 'Could not start settlement. Please try again.';
      alert(message);
      setSettling(false);
    }
  };

  const handlePaymentClick = () => {
    if (pendingPayment) {
      setIsPaymentModalOpen(true);
      return;
    }

    // No open installment found yet — open payment history focused on this loan
    if (activeLoan?.id) {
      navigate(
        `/dashboard/payments?status=pending&loanId=${encodeURIComponent(activeLoan.id)}`
      );
      return;
    }

    navigate('/dashboard/payments?status=pending');
  };

  const handlePaymentMethodSelect = (method: 'payfast' | 'debit_order') => {
    setIsPaymentModalOpen(false);
    
    if (!pendingPayment) return;

    switch (method) {
      case 'payfast':
        navigate(`/payment/${pendingPayment.id}/payfast`);
        break;
      case 'debit_order':
        navigate('/payment/debit-order/setup');
        break;
    }
  };

  if (state === 'LOADING') {
    return (
      <div className="student-dashboard">
        <div className="dashboard-header-minimal">
            <div className="loading-pulse" style={{width: '150px', height: '24px', background: '#e2e8f0', borderRadius: '4px'}}></div>
        </div>
        <div className="hero-card" style={{height: '200px', background: '#f1f5f9'}}></div>
      </div>
    );
  }

  return (
    <div className="student-dashboard">
      {/* Header with Notifications */}
      <header className="dashboard-header-minimal">
        <div className="user-greeting">
          <h1>Hi, {user?.first_name || 'Student'}</h1>
          <p>{new Date().toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
      </header>

      {/* Dynamic Hero Card */}
      {state === 'ELIGIBLE' && (
        <>
          <div className="hero-card eligibility">
            {/* Small inline note for recently repaid loan */}
            {activeLoan && activeLoan.status === 'repaid' && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                background: '#ecfdf5', borderRadius: '8px', padding: '0.5rem 0.75rem',
                marginBottom: '1rem', fontSize: '0.85rem', color: '#065f46'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#10B981" strokeWidth="2"/>
                  <path d="M8 12l3 3 5-6" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Previous loan #{activeLoan.loanNumber} paid off
              </div>
            )}
            <div className="hero-icon">
              <Icons.Wallet />
            </div>
            <h2 className="hero-subtitle">You qualify for up to</h2>
            <div className="hero-amount">
              R {Math.min(750, eligibility?.maxAllowedAmount ?? 750).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
            </div>
            <button className="primary-cta" onClick={handleApply}>
              Get Cash Now
            </button>
            <p style={{marginTop: '1rem', fontSize: '0.85rem', opacity: 0.9}}>
              Instant approval • Repay when NSFAS pays
            </p>
          </div>
        </>
      )}

      {state === 'ACTIVE_LOAN' && activeLoan && activeLoan.outstandingBalance > 0 && (
        <div className="hero-card active-loan hero-split">
          <div className="hero-balance-panel">
            <div className="loan-balance-label">Outstanding Balance</div>
            <div className="loan-balance-amount">
              {formatMoney(activeLoan.outstandingBalance)}
            </div>

            <div className="loan-due-date">
              <span>Next payment due</span>
              <span className="due-warning">
                <Icons.Calendar />
                {formatShortDate(pendingPayment?.dueDate || activeLoan.repaymentDate) || 'TBC'}
              </span>
            </div>

            <button className="repay-cta" onClick={handlePaymentClick}>
              <Icons.CreditCard />
              {pendingPayment ? `Pay ${formatMoney(pendingPayment.amount)}` : 'Make Payment'}
              <Icons.ArrowRight />
            </button>

            {canSettleEarly && (
              <button
                type="button"
                className="repay-cta repay-cta--settle"
                onClick={handleSettleEarly}
                disabled={settling}
              >
                {settling ? 'Starting…' : `Settle full balance ${formatMoney(activeLoan.outstandingBalance)}`}
              </button>
            )}

            {debicheckPrompt === 'setup' && (
              <button
                type="button"
                className="repay-cta repay-cta--debicheck"
                onClick={() => navigate('/payment/debit-order/setup')}
              >
                Set up DebiCheck debit order
                <Icons.ArrowRight />
              </button>
            )}
            {debicheckPrompt === 'pending' && (
              <p className="secure-payment-note" style={{ marginTop: '0.75rem' }}>
                DebiCheck setup submitted — waiting for NuPay / bank approval.
              </p>
            )}
            {debicheckPrompt === 'authorised' && (
              <p className="secure-payment-note" style={{ marginTop: '0.75rem' }}>
                DebiCheck authorised — monthly debits will run from your bank.
              </p>
            )}

            <div className="secure-payment-note">
              <Icons.Lock /> Secure payment
            </div>
          </div>

          <div className="hero-progress-panel">
            <div className="progress-panel-header">
              <h3>Repayment Progress</h3>
              {installments && installments.total > 1 && (
                <span className="installment-pill">
                  Installment {Math.min(installments.completed + 1, installments.total)} of {installments.total}
                </span>
              )}
            </div>

            <div className="progress-panel-body">
              <div
                className="progress-donut"
                style={{ background: `conic-gradient(#2563eb 0% ${paidPercent}%, #e2e8f0 ${paidPercent}% 100%)` }}
                role="img"
                aria-label={`${paidPercent}% paid`}
              >
                <div className="progress-donut-inner">
                  <strong>{paidPercent}%</strong>
                  <span>Paid</span>
                </div>
              </div>

              <div className="progress-legend">
                <div className="legend-row">
                  <span><i className="dot dot-principal" /> Original loan</span>
                  <strong>{formatMoney(activeLoan.principalAmount)}</strong>
                </div>
                <div className="legend-row">
                  <span><i className="dot dot-interest" /> Interest ({activeLoan.interestRate > 0 && activeLoan.interestRate < 1 ? Math.round(activeLoan.interestRate * 100) : activeLoan.interestRate}%)</span>
                  <strong>{formatMoney(activeLoan.interestAmount)}</strong>
                </div>
                <div className="legend-row">
                  <span><i className="dot dot-fee" /> Admin fee</span>
                  <strong>{formatMoney(activeLoan.adminFee)}</strong>
                </div>
                <div className="legend-divider" />
                <div className="legend-row paid">
                  <span><i className="dot dot-paid" /> Paid so far</span>
                  <strong>{formatMoney(activeLoan.totalPaid)}</strong>
                </div>
                <div className="legend-row remaining">
                  <span>Remaining after this payment</span>
                  <strong>{formatMoney(remainingAfterPayment)}</strong>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="loan-details-toggle"
              onClick={() => setShowLoanDetails(!showLoanDetails)}
            >
              <span>Loan Summary</span>
              <span className={`chevron ${showLoanDetails ? 'open' : ''}`}>
                <Icons.ChevronDown />
              </span>
            </button>

            {showLoanDetails && (
              <div className="loan-breakdown">
                <div className="breakdown-row total">
                  <span className="breakdown-label">Total Loan</span>
                  <span className="breakdown-value">{formatMoney(activeLoan.totalAmount)}</span>
                </div>
                <div className="breakdown-row">
                  <span className="breakdown-label">Loan #</span>
                  <span className="breakdown-value">{activeLoan.loanNumber}</span>
                </div>
                <div className="breakdown-row outstanding">
                  <span className="breakdown-label">Outstanding</span>
                  <span className="breakdown-value highlight">{formatMoney(activeLoan.outstandingBalance)}</span>
                </div>
              </div>
            )}

            {eligibility?.eligible && (
              <button
                type="button"
                className="secondary-cta-inline"
                onClick={() => navigate('/apply')}
              >
                Need more cash? Apply for another loan
              </button>
            )}
          </div>
        </div>
      )}

      {/* Fully-paid loans are now filtered out in the state logic above,
         so ACTIVE_LOAN always has outstandingBalance > 0 */}

      {state === 'APPLICATION_FEEDBACK' && feedbackLoan && (
        <div className="hero-card application-feedback">
          <div className="feedback-status">
            <div className="hero-icon-feedback">
              {feedbackLoan.status === 'requires_info' ? <Icons.Info /> : <Icons.AlertCircle />}
            </div>
            <h3>
              {feedbackLoan.status === 'requires_info'
                ? 'We need more information'
                : 'Application not approved'}
            </h3>
            <p className="feedback-ref">Reference #{feedbackLoan.loanNumber}</p>
            {feedbackLoan.rejectionReason ? (
              <div className="feedback-message">
                <strong>Feedback from GenLend</strong>
                <p>{feedbackLoan.rejectionReason}</p>
              </div>
            ) : (
              <p style={{ color: '#64748b' }}>
                {feedbackLoan.status === 'requires_info'
                  ? 'Please read the notes below and submit a new application with the correct documents.'
                  : 'You can submit a new application and upload updated documents if you wish to try again.'}
              </p>
            )}
            {feedbackDocuments.filter((d: any) => d.verification_status === 'rejected').length > 0 && (
              <div className="feedback-doc-list">
                <strong>Documents</strong>
                <ul>
                  {feedbackDocuments
                    .filter((d: any) => d.verification_status === 'rejected')
                    .map((d: any) => (
                      <li key={d.id}>
                        <span className="doc-name">{DOC_TYPE_LABELS[d.document_type] || d.document_type}</span>
                        {d.rejection_reason ? (
                          <span className="doc-reason">{d.rejection_reason}</span>
                        ) : (
                          <span className="doc-reason">Needs to be replaced</span>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            )}
            <p className="feedback-hint">
              Submit a new loan application to upload fresh documents. Your previous upload stays on file for our records.
            </p>
            <button type="button" className="primary-cta" onClick={handleApply}>
              Start new application
            </button>
          </div>
        </div>
      )}

      {state === 'AWAITING_DISBURSEMENT' && activeLoan && (
        <div className="hero-card pending">
          <div className="pending-status">
            <div className="hero-icon-pending">
              <Icons.Clock />
            </div>
            <h3>Loan approved</h3>
            <p style={{ color: '#64748b' }}>
              Loan #{activeLoan.loanNumber} is approved. We’re preparing to send{' '}
              {new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(
                activeLoan.principalAmount || activeLoan.totalAmount
              )}{' '}
              to your bank account. Your repayment schedule will appear here once funds are disbursed.
            </p>
            <div className="step-indicator">Awaiting disbursement</div>
            {debicheckPrompt === 'setup' && (
              <button
                type="button"
                className="repay-cta"
                style={{ marginTop: '1rem' }}
                onClick={() => navigate('/payment/debit-order/setup')}
              >
                Set up DebiCheck now
              </button>
            )}
            {debicheckPrompt === 'pending' && (
              <p style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#0f766e' }}>
                DebiCheck details submitted — waiting for NuPay / bank approval.
              </p>
            )}
            {debicheckPrompt === 'authorised' && (
              <p style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#0f766e' }}>
                DebiCheck authorised for this loan.
              </p>
            )}
          </div>
        </div>
      )}

      {state === 'PENDING_LOAN' && (
        <div className="hero-card pending">
          <div className="pending-status">
            <div className="hero-icon-pending">
              <Icons.Clock />
            </div>
            <h3>Application Under Review</h3>
            <p style={{color: '#64748b'}}>We're verifying your documents. This usually takes 2-4 hours.</p>
            {pendingDocIssues.length > 0 && (
              <div className="pending-doc-issues">
                <strong>Update on your documents</strong>
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.9rem', color: '#9a3412' }}>
                  Some files need attention before we can approve your loan:
                </p>
                <ul>
                  {pendingDocIssues.map((issue, idx) => (
                    <li key={`${issue.label}-${idx}`}>
                      <span className="doc-name">{issue.label}</span>
                      {issue.reason ? <span className="doc-reason">{issue.reason}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="step-indicator">Step 3 of 4: Verification</div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="action-grid">
        <button className="action-btn" onClick={() => navigate('/')}>
          <div className="action-icon action-icon--calc">
            <Icons.Calculator />
          </div>
          <div className="action-copy">
            <span className="action-label">Estimate a Loan</span>
            <span className="action-desc">See how much you could borrow</span>
          </div>
          <Icons.ChevronRight />
        </button>

        <button className="action-btn" onClick={() => navigate('/dashboard/history')}>
          <div className="action-icon action-icon--history">
            <Icons.History />
          </div>
          <div className="action-copy">
            <span className="action-label">Loan History</span>
            <span className="action-desc">View your loans and payment history</span>
          </div>
          <Icons.ChevronRight />
        </button>

        <button className="action-btn" onClick={() => navigate('/dashboard/support')}>
          <div className="action-icon action-icon--support">
            <Icons.Message />
          </div>
          <div className="action-copy">
            <span className="action-label">Support</span>
            <span className="action-desc">Get help or contact our team</span>
          </div>
          <Icons.ChevronRight />
        </button>
      </div>

      {/* Secondary row: upcoming + tip (data we already have) */}
      <div className="dashboard-secondary-grid">
        {pendingPayment && (
          <div className="upcoming-payment-card">
            <div className="upcoming-icon">
              <Icons.Calendar />
            </div>
            <div className="upcoming-copy">
              <div className="upcoming-title">Upcoming Payment</div>
              <div className="upcoming-meta">
                {nextDueDays === null
                  ? 'Due date TBC'
                  : nextDueDays < 0
                    ? `Overdue by ${Math.abs(nextDueDays)} day${Math.abs(nextDueDays) === 1 ? '' : 's'}`
                    : nextDueDays === 0
                      ? 'Due today'
                      : `In ${nextDueDays} day${nextDueDays === 1 ? '' : 's'}`}
                {(pendingPayment.dueDate || activeLoan?.repaymentDate) && (
                  <span> · {formatShortDate(pendingPayment.dueDate || activeLoan?.repaymentDate)}</span>
                )}
              </div>
            </div>
            <div className="upcoming-amount">
              <strong>{formatMoney(pendingPayment.amount)}</strong>
              <span>
                {pendingPayment.paymentMethod === 'debit_order' || pendingPayment.paymentMethod === 'debicheck'
                  ? 'Debit order'
                  : pendingPayment.paymentMethod === 'payfast'
                    ? 'PayFast'
                    : 'Payment due'}
              </span>
            </div>
          </div>
        )}

        <div className="tip-card tip-card--highlight">
          <div className="tip-icon">
            <Icons.Lightbulb />
          </div>
          <div className="tip-content">
            <h4>Tip of the day</h4>
            <p>
              Paying on time builds your GenLend repayment history and can unlock faster approvals
              for your next loan — no credit bureau pull required.
            </p>
          </div>
        </div>
      </div>

      {recentActivity.length > 0 && (
        <div className="recent-activity-card">
          <div className="recent-activity-header">
            <h3>Recent Activity</h3>
            <button type="button" onClick={() => navigate('/dashboard/history')}>View all</button>
          </div>
          <ul className="recent-activity-list">
            {recentActivity.map((item) => (
              <li key={item.id} className={`activity-item activity-item--${item.tone}`}>
                <div className="activity-icon">
                  {item.tone === 'success' ? <Icons.CheckCircle /> : <Icons.Clock />}
                </div>
                <div className="activity-copy">
                  <strong>{item.title}</strong>
                  <span>{item.subtitle}</span>
                </div>
                <div className="activity-meta">
                  {item.amount != null && (
                    <strong className={item.tone === 'success' ? 'amount-success' : ''}>
                      {formatMoney(item.amount)}
                    </strong>
                  )}
                  <span>{formatShortDate(item.date)}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="support-banner">
        <div className="support-banner-copy">
          <Icons.Lock />
          <div>
            <strong>We're here to help</strong>
            <p>If you have any questions or need assistance, our support team is ready to help.</p>
          </div>
        </div>
        <button type="button" className="support-banner-cta" onClick={() => navigate('/dashboard/support')}>
          <Icons.Headset /> Contact Support
        </button>
      </div>

      <PaymentMethodModal 
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSelectMethod={handlePaymentMethodSelect}
        amount={pendingPayment?.amount || 0}
      />
    </div>
  );
};

export default StudentDashboard;
